<template>
  <div class="gd-section">
    <div class="gd-section-head">
      <span class="n">§3</span>
      <span class="t">Solicitante</span>
      <span class="d">
        Campos 15–19 do padrão TISS
        <template v-if="required"> · obrigatórios na SP/SADT</template>
        <template v-else> · opcionais na consulta</template>
      </span>
    </div>
    <div class="gd-section-body">
      <div class="columns" v-if="readonly">
        <div class="column col-6 col-sm-12">
          <guide-read-field label="15 · Profissional solicitante" :value="professional.name"/>
        </div>
        <div class="column col-3 col-sm-6">
          <guide-read-field label="16–18 · Conselho" :value="councilText"/>
        </div>
        <div class="column col-3 col-sm-6">
          <guide-read-field label="19 · Especialidade (CBO)" :value="specialtyText"/>
        </div>
      </div>
      <div class="columns" v-else>
        <div class="column col-6 col-md-12 form-group"
             :class="{'has-error': err('request.professional.name')}">
          <label class="form-label">
            15 · Profissional solicitante
            <a class="c-hand ml-2 text-secondary text-small" @click="referralManager = true">
              [Gerenciar solicitantes]
            </a>
          </label>
          <dx-autocomplete
            :input-id="fieldId('request.professional.name')"
            v-model="picked"
            :source="findRequester"
            label="name"
            track-by="key"
            :debounce="600"
            :readonly="!!professional.name"
            @select="pickRequester"
            @blur="touch('request.professional.name')"
            placeholder="Nome ou nº do conselho (internos e externos)">
            <template v-slot="{ item }">
              <a>
                <fa-icon :icon="['fal', item.internal ? 'user-md' : 'user']" class="text-primary"/>
                <span class="ml-1">{{ item.name }}</span>
                <small class="text-gray ml-1" v-if="item.council && item.council.record">
                  {{ item.council.name }}-{{ item.council.state }} {{ item.council.record }}
                </small>
              </a>
            </template>
            <template v-slot:action>
              <button type="button" class="btn btn-gray btn-action input-group-btn btn-icon"
                      tabindex="-1" v-if="professional.name" @click="clearRequester">
                <fa-icon :icon="['fal', 'times']"/>
              </button>
              <button type="button"
                      class="btn btn-neutral input-group-btn tooltip tooltip-left"
                      data-tooltip="Preencher com o executante" tabindex="-1" v-else
                      @click="useExecutant">
                Eu mesmo
              </button>
            </template>
          </dx-autocomplete>
          <div class="form-input-hint" v-if="err('request.professional.name')">
            {{ hint('request.professional.name') }}
          </div>
        </div>
        <div class="column col-2 col-md-4 col-sm-6 form-group"
             :class="{'has-error': err('request.professional.council.name')}">
          <label class="form-label">16 · Conselho</label>
          <select class="form-select" v-model="professional.council.name"
                  :id="fieldId('request.professional.council.name')"
                  :disabled="locked" @blur="touch('request.professional.council.name')">
            <option value="">[Selecione]</option>
            <option v-for="(text, value) in councils" :value="value" :key="value">
              {{ value }}
            </option>
          </select>
          <div class="form-input-hint" v-if="err('request.professional.council.name')">
            {{ hint('request.professional.council.name') }}
          </div>
        </div>
        <div class="column col-2 col-md-4 col-sm-6 form-group"
             :class="{'has-error': err('request.professional.council.record')}">
          <label class="form-label">17 · Registro</label>
          <input type="text" class="form-input" v-model="professional.council.record"
                 :id="fieldId('request.professional.council.record')"
                 :readonly="locked" @blur="touch('request.professional.council.record')">
          <div class="form-input-hint" v-if="err('request.professional.council.record')">
            {{ hint('request.professional.council.record') }}
          </div>
        </div>
        <div class="column col-2 col-md-4 col-sm-12 form-group"
             :class="{'has-error': err('request.professional.council.state')}">
          <label class="form-label">18 · UF</label>
          <select class="form-select" v-model="professional.council.state"
                  :id="fieldId('request.professional.council.state')"
                  :disabled="locked" @blur="touch('request.professional.council.state')">
            <option value="">[UF]</option>
            <option v-for="(text, value) in states" :value="value" :key="value">{{ value }}</option>
          </select>
          <div class="form-input-hint" v-if="err('request.professional.council.state')">
            {{ hint('request.professional.council.state') }}
          </div>
        </div>
        <div class="column col-6 col-md-12 form-group"
             :class="{'has-error': err('request.professional.specialty.code')}">
          <label class="form-label">19 · Especialidade (CBO)</label>
          <dx-autocomplete
            :input-id="fieldId('request.professional.specialty.code')"
            v-model="specialty"
            :source="findCbo"
            label="name"
            track-by="code"
            :debounce="600"
            :readonly="!!professional.specialty.code"
            @select="pickCbo"
            @blur="touch('request.professional.specialty.code')"
            placeholder="Código ou nome do CBO">
            <template v-slot="{ item }">
              <a>{{ item.code }} · {{ item.name }}</a>
            </template>
            <template v-slot:action>
              <button type="button" class="btn btn-gray btn-action input-group-btn btn-icon"
                      tabindex="-1" :disabled="!professional.specialty.code" @click="clearCbo">
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </template>
          </dx-autocomplete>
          <div class="form-input-hint" v-if="err('request.professional.specialty.code')">
            {{ hint('request.professional.specialty.code') }}
          </div>
        </div>
      </div>
    </div>
    <referral-manager-modal
      v-if="referralManager"
      :show="referralManager"
      @close="referralManager = false"
    />
  </div>
</template>

<script>
import { councils } from '@/data/council-types';
import states from '@/data/states';
import fieldMixin from '../fieldMixin';
import GuideReadField from './GuideReadField.vue';
import ReferralManagerModal from '../../../referral/components/ManagerModal.vue';

function requesterKey(item) {
  const council = item.council || {};
  if (council.record) {
    return `${council.name || ''}|${council.state || ''}|${council.record}`.toLowerCase();
  }
  return `name|${(item.name || '').trim().toLowerCase()}`;
}

export default {
  mixins: [fieldMixin],
  components: {
    GuideReadField,
    ReferralManagerModal,
  },
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    professionals: {
      type: Array,
      default: () => [],
    },
    executant: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      referralManager: false,
      councils,
      states,
      picked: null,
      specialty: null,
    };
  },
  computed: {
    professional() {
      return this.form.request.professional;
    },
    locked() {
      return !!this.form.request.professionalId;
    },
    councilText() {
      const { council } = this.professional;
      if (!council || !council.record) return '';
      return `${council.name || ''}-${council.state || ''} ${council.record}`.trim();
    },
    specialtyText() {
      const { specialty } = this.professional;
      if (!specialty || !specialty.code) return '';
      return specialty.name ? `${specialty.code} · ${specialty.name}` : specialty.code;
    },
  },
  mounted() {
    this.syncInputs();
  },
  methods: {
    syncInputs() {
      this.picked = this.professional.name
        ? { key: 'current', name: this.professional.name }
        : null;
      this.specialty = this.professional.specialty.code
        ? { ...this.professional.specialty }
        : null;
    },
    internalMatches(search) {
      const term = (search || '').toLowerCase();
      return this.professionals
        .filter(item => item.name.toLowerCase().includes(term))
        .slice(0, 10)
        .map(item => ({
          key: `internal-${item.id}-${item.specialty ? item.specialty.code : ''}`,
          internal: true,
          id: item.id,
          name: item.name,
          council: item.council,
          specialty: item.specialty,
        }));
    },
    findRequester(search) {
      const internal = this.internalMatches(search);
      return this.$http.get('/referrals', { params: { search, limit: 10 } })
        .then(({ data }) => data.items.map(item => ({
          key: `external-${item.id}`,
          internal: false,
          id: null,
          name: item.name,
          council: item.council,
          specialty: item.specialty,
        })))
        .catch(() => [])
        .then((external) => {
          const seen = new Set(internal.map(item => requesterKey(item)));
          return [
            ...internal,
            ...external.filter((item) => {
              const key = requesterKey(item);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            }),
          ];
        });
    },
    apply(source, internalId = null) {
      this.form.request.professionalId = internalId;
      this.professional.name = source.name || '';
      this.professional.council.name = source.council ? source.council.name || '' : '';
      this.professional.council.state = source.council ? source.council.state || '' : '';
      this.professional.council.record = source.council ? source.council.record || '' : '';
      this.professional.specialty.code = source.specialty ? source.specialty.code || '' : '';
      this.professional.specialty.name = source.specialty ? source.specialty.name || '' : '';
      this.syncInputs();
      this.$emit('change');
    },
    pickRequester(item) {
      if (!item) return;
      this.apply(item, item.internal ? item.id : null);
    },
    clearRequester() {
      this.apply({ name: '', council: null, specialty: null });
    },
    useExecutant() {
      if (!this.executant) {
        this.$toast.error('Informe o profissional executante.');
        return;
      }
      this.apply(this.executant, this.executant.id);
    },
    findCbo(search) {
      return this.$http.get('/services/cbos', { params: { search, isSpecialty: true } })
        .then(({ data }) => data.items);
    },
    pickCbo(item) {
      if (!item) return;
      this.professional.specialty.code = item.code;
      this.professional.specialty.name = item.name;
      this.specialty = { code: item.code, name: item.name };
      this.$emit('change');
    },
    clearCbo() {
      this.professional.specialty.code = '';
      this.professional.specialty.name = '';
      this.specialty = null;
    },
  },
};
</script>
